import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createEmbedLicense, getEmbedHealth, listEmbedLicenses, me, patchMe, revokeEmbedLicense } from '../api/portal';
import { ApiError } from '../api/client';
import { isLeagueLogin } from '../lib/portalRole';

export function SitesPage() {
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const health = useQuery({ queryKey: ['portal-embed-health'], queryFn: getEmbedHealth, refetchInterval: 20_000 });
  const [origins, setOrigins] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) setOrigins((profile.data.allowed_origins ?? []).join('\n'));
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () => patchMe({
      allowed_origins: origins.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      setMsg('Saved');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Access</div>
        <h1 className="page-title">My sites</h1>
        <p className="page-sub">
          Widgeti rade samo sa ovih hostova. Posle paste-a, Origo sam kaže da li se live-score montirao.
        </p>
      </div>
      <form onSubmit={onSubmit} className="card p-4 space-y-3 max-w-xl">
        <label className="label">Allowed origins (one per line)</label>
        <textarea
          className="textarea min-h-[120px]"
          value={origins}
          onChange={(e) => setOrigins(e.target.value)}
          placeholder={"scores.betsite.rs\nwww.betsite.rs"}
        />
        {msg && <div className="text-sm text-slate-300">{msg}</div>}
        <button className="btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </form>
      <div className="card mt-4 p-4 max-w-xl">
        <div className="mb-2 text-[11px] uppercase text-slate-500">Embed health</div>
        {(health.data?.sites ?? []).map((s) => (
          <div key={`${s.origin}-${s.widget}`} className="flex justify-between py-1 text-sm">
            <span className="text-white">{s.origin} · {s.widget}</span>
            <span className={s.state === 'live' ? 'text-emerald-300' : s.state === 'never' || s.state === 'stale' ? 'text-rose-300' : 'text-slate-400'}>
              {s.state === 'live' ? `montiran ${s.age || 'sada'}` : s.state === 'seen' ? `viđen ${s.age}` : s.state === 'stale' ? 'starije od 7 dana' : 'nije montiran'}
            </span>
          </div>
        ))}
        {(health.data?.missing ?? []).map((o) => (
          <div key={o} className="flex justify-between py-1 text-sm">
            <span className="text-white">{o}</span>
            <span className="text-rose-300">još nije</span>
          </div>
        ))}
        {(health.data?.sites ?? []).length === 0 && (health.data?.missing ?? []).length === 0 && (
          <div className="text-sm text-slate-500">Nema mount-a. Ubaci widget pa osveži.</div>
        )}
      </div>
      {isLeagueLogin(profile.data) && <EmbedLicenseCard />}
    </div>
  );
}

function EmbedLicenseCard() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['portal-embed-licenses'], queryFn: listEmbedLicenses });
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => createEmbedLicense({ site_name: name.trim() || origin.trim(), origin: origin.trim() }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-embed-licenses'] });
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      setName('');
      setOrigin('');
      setErr(res.note);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  const revoke = useMutation({
    mutationFn: revokeEmbedLicense,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-embed-licenses'] });
      qc.invalidateQueries({ queryKey: ['portal-me'] });
    },
  });
  const d = list.data;
  return (
    <div className="card mt-4 p-4 max-w-xl">
      <div className="mb-1 text-[11px] uppercase text-slate-500">Zvanična licenca za embed</div>
      <p className="mb-3 text-xs text-slate-400">
        Liga izdaje pravo portalu da ugradi widget. Host ulazi u allowlist, a widget na tom hostu dobija oznaku Zvanično.
        {d && ` Aktivno ${d.active}, uračunato ${d.included}, Origo naplaćuje ${d.origo_invoice_eur} € (${d.origo_fee_eur} € po dodatnom hostu).`}
      </p>
      <form className="mb-3 flex flex-wrap gap-2" onSubmit={(e: FormEvent) => { e.preventDefault(); add.mutate(); }}>
        <input className="input w-40" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naziv sajta" />
        <input className="input flex-1" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="scores.portal.rs" required />
        <button className="btn-primary" disabled={add.isPending || !origin.trim()}>Izdaj</button>
      </form>
      {err && <div className="mb-2 text-sm text-slate-300">{err}</div>}
      {(d?.licenses ?? []).map((lic) => (
        <div key={lic.id} className="flex items-center justify-between py-1 text-sm">
          <span className={lic.active ? 'text-white' : 'text-slate-500'}>{lic.site_name} · {lic.origin}</span>
          {lic.active
            ? <button type="button" className="btn-ghost text-xs" onClick={() => revoke.mutate(lic.id)}>Povuci</button>
            : <span className="text-xs text-slate-500">povućeno</span>}
        </div>
      ))}
    </div>
  );
}
